import { BYTES_PER_GIGABYTE } from "@/lib/fileSize"

export type BillingPlanId = "free" | "pro" | "business" | "team" | "enterprise"
export type BillingPlanCredits = number | "custom"
export type BillingPlanDuration = number | "custom" | "unlimited"
export type BillingPlanParticipantLimit = number | "custom"
export type BillingPlanPrice = number | null
export type BillingPlanRecordingStorageBytes = number | "custom"

export type BillingLookupKey =
  | "pro_monthly"
  | "pro_yearly"
  | "business_monthly"
  | "business_yearly"

export interface BillingPlanFeatures {
  ai_credits: BillingPlanCredits
  reward_multiplier: number
  max_participants: BillingPlanParticipantLimit
  duration: BillingPlanDuration
  noise_cancellation: boolean
  profile_badge: boolean
  priority_support: boolean
  seats_included?: number
  add_seat_credits?: number
  base_plan_credits?: number
  pooled_ai_credits?: boolean
  shared_meeting_history?: boolean
  roles_admin_console?: boolean
  sso_saml?: boolean
  custom_retention_policies?: boolean
  private_deployment_sla?: boolean
  dedicated_account_manager?: boolean
  recording: boolean
  recording_storage_bytes: BillingPlanRecordingStorageBytes
}

export const BILLING_PLAN_RECORDING_FEATURES: Record<
  BillingPlanId,
  Pick<BillingPlanFeatures, "recording" | "recording_storage_bytes">
> = {
  free: { recording: false, recording_storage_bytes: 0 },
  pro: { recording: true, recording_storage_bytes: 20 * BYTES_PER_GIGABYTE },
  business: { recording: true, recording_storage_bytes: 40 * BYTES_PER_GIGABYTE },
  team: { recording: true, recording_storage_bytes: 250 * BYTES_PER_GIGABYTE },
  enterprise: { recording: true, recording_storage_bytes: "custom" },
}

export interface BillingPlan {
  id: BillingPlanId
  isPopular?: boolean
  features: BillingPlanFeatures
  prices: {
    monthly: BillingPlanPrice
    yearly: BillingPlanPrice
  }
  seats?: {
    included: number
    min: number
    max: number
    creditsPerSeat: number
    recordingStoragePerSeat?: number
    addSeatPrices: {
      monthly: BillingPlanPrice
      yearly: BillingPlanPrice
    }
    addSeatLookupKeys: {
      monthly: string | null
      yearly: string | null
    }
  }
  lookupKeys: {
    monthly: BillingLookupKey | null
    yearly: BillingLookupKey | null
  }
  currency: string
}

export const AI_CREDIT_COST = {
  memory: 10,
  summary: 2,
  translation: 1,
} as const

export const PER_UNIT_COST = {
  askAi: { cost: AI_CREDIT_COST.memory, suffixKey: "billingPage.perQuery" },
  summary: { cost: AI_CREDIT_COST.summary, suffixKey: "billingPage.perMin" },
  transcription: {
    cost: AI_CREDIT_COST.translation,
    suffixKey: "billingPage.perMin",
  },
} as const

export const paidPlansIds: (BillingPlanId | null)[] = [
  "pro",
  "business",
  "team",
  "enterprise",
]
